import React, { forwardRef } from 'react';
import { cn } from '@/lib/utils';

const variantStyles = {
  default:
    'border border-[color:var(--inst-player-border,#e5e7eb)] bg-[color:var(--inst-player-surface,#ffffff)]',
  outlined:
    'border-2 border-[color:var(--inst-player-border,#d1d5db)] bg-transparent',
  elevated:
    'border border-[color:var(--inst-player-border,#e5e7eb)] bg-[color:var(--inst-player-surface,#ffffff)] shadow-lg',
} as const;

export type CardVariant = keyof typeof variantStyles;

export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: CardVariant;
  hoverable?: boolean;
}

export interface CardSectionProps extends React.HTMLAttributes<HTMLDivElement> {}

export const Card = forwardRef<HTMLDivElement, CardProps>(
  ({ className, variant = 'default', hoverable = false, children, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        'rounded-xl overflow-hidden transition-all',
        variantStyles[variant],
        hoverable && ['cursor-pointer', 'hover:shadow-md hover:-translate-y-0.5'],
        className
      )}
      {...props}
    >
      {children}
    </div>
  )
);

export const CardHeader = forwardRef<HTMLDivElement, CardSectionProps>(
  ({ className, children, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        'border-b border-[color:var(--inst-player-border,#e5e7eb)] px-6 py-4',
        className,
      )}
      {...props}
    >
      {children}
    </div>
  )
);

export const CardBody = forwardRef<HTMLDivElement, CardSectionProps>(
  ({ className, children, ...props }, ref) => (
    <div ref={ref} className={cn('px-6 py-4', className)} {...props}>
      {children}
    </div>
  )
);

export const CardFooter = forwardRef<HTMLDivElement, CardSectionProps>(
  ({ className, children, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        'border-t border-[color:var(--inst-player-border,#e5e7eb)] px-6 py-4',
        className
      )}
      {...props}
    >
      {children}
    </div>
  )
);

Card.displayName = 'Card';
CardHeader.displayName = 'CardHeader';
CardBody.displayName = 'CardBody';
CardFooter.displayName = 'CardFooter';
